import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import sortBy from 'lodash/sortBy';
import snakeCase from 'lodash/snakeCase';
import keyBy from 'lodash/keyBy';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import cn from 'classnames';
import dayjs from 'dayjs';
import { ColumnSort } from 'legacy-packages/ft-cb';

import dateTimeFormat from 'legacy-src/utility/DateTime';
import { learnosityReportByUsers } from 'legacy-src/utility/progressHelpers';
import { getPriorStudentsSubdir } from 'legacy-src/utility/utils';
import { isStudentSubmissionLate } from 'chameleon/ui-stack/ui/assessments/util';

import SPPStudentPerformanceAnswers from './SPPStudentPerformanceAnswers';
import TableSorter from './tableSorter';
import './index.scss';

const MatrixColumSort = ({ children, ...props }) => (
  <ColumnSort {...props} isDisabled={false} sortIconColor="black">
    {children}
  </ColumnSort>
);

const renderHeaderOneColumn = (item) => {
  const { id, title, learnosityItemReference } = item;
  return (
    <span
      data-itemid={id}
      data-reference={learnosityItemReference}
      data-ypos="top"
      role="link"
      tabIndex="0"
    >
      {title}
    </span>
  );
};

const renderHeaderTwoColumn = (item, otherProps = {}) => {
  const { title } = item;
  return (
    <MatrixColumSort {...item} {...otherProps}>
      {title}
    </MatrixColumSort>
  );
};

const SPPStudentPerformanceTable = (props) => {
  const {
    items,
    params,
    students: groupStudents,
    routes,
    assignment,
    learnosityReportsData,
    learnosityReportsByItemData,
    sessionTemplate,
    userData,
    studentResponses,
    router,
  } = props;

  const { students: assignmentStudents, customAssignmentSettings } = assignment;
  const students = sortBy(assignmentStudents || groupStudents, [
    'lastName',
    'firstName',
  ]);
  const [studentItems, setStudentItems] = useState(students);
  const [tableSortSelection, setTableSortSelection] = useState('');
  const tableSorter = useRef(null);

  const { location } = router || {};
  const { pathname, search } = location || {};
  const returnPath = `${pathname}${search}`;

  useEffect(() => {
    tableSorter.current = new TableSorter(
      students,
      resultsByUser,
      studentSessionsByUserId,
      studentResponses,
      itemResultsByUser,
    );
  }, [students]);

  const studentsTable = useRef();

  const { items: learnosityItems = [] } = sessionTemplate || {};
  const sessionTemplateItemsByRef = learnosityItems.reduce(
    (acc, currentValue) => {
      const { reference } = currentValue;
      acc[reference] = currentValue;
      return acc;
    },
    {},
  );

  const customSettingsByStudentId = useMemo(
    () =>
      keyBy(
        customAssignmentSettings.map((item) =>
          Object.entries(item).reduce((acc, [key, value]) => {
            acc[snakeCase(key)] = value;
            return acc;
          }, {}),
        ),
        'user_id',
      ),
    [customAssignmentSettings],
  );

  const resultsByUser = learnosityReportByUsers(learnosityReportsData);
  const itemResultsByUser = learnosityReportByUsers(
    learnosityReportsByItemData,
  );

  const studentSessionsByUserId =
    assignment.studentSessions &&
    assignment.studentSessions.reduce(
      (acc, session) => ({
        ...acc,
        [session.userId]: session,
      }),
      {},
    );

  const headerOne = [
    {
      key: 'name-blank',
      children: <span />,
      scope: 'col',
    },
    {
      key: 'points-blank',
      children: <span />,
      scope: 'col',
    },
    {
      key: 'submitted-blank',
      children: <span />,
      scope: 'col',
    },
  ];
  const headerTwo = [];

  const onTableSortChange = (tableSort) => {
    const sortedItems = tableSorter.current.sortStudent(tableSort);
    setStudentItems(sortedItems);
    setTableSortSelection(tableSort);
  };

  const otherProps = {
    selection: tableSortSelection,
    onChange: onTableSortChange,
    sessionTemplate,
  };

  items.forEach((item) => {
    const { learnosityItemReference } = item || {};
    headerOne.push({
      key: learnosityItemReference,
      itemId: item.id,
      children: renderHeaderOneColumn(item),
    });
    headerTwo.push({
      key: `${learnosityItemReference}-answer`,
      children: renderHeaderTwoColumn({
        title: `Answer`,
        columnId: `answer-${learnosityItemReference}`,
        ...otherProps,
      }),
      scope: 'col',
      className: 'th_spp_part',
    });
    let tipCount = 1;

    const questions = get(
      sessionTemplateItemsByRef,
      `[${learnosityItemReference}].questions`,
      [],
    );

    questions.forEach((question) => {
      const { type, response_id } = question || {};
      if (type !== 'formulaessayV2' && type !== 'clozedropdown') return;

      headerOne.push({
        key: `${learnosityItemReference}-tip-${tipCount}-blank`,
        children: '',
        scope: 'col',
      });

      headerTwo.push({
        key: `${learnosityItemReference}-tip-${tipCount}`,
        children: renderHeaderTwoColumn({
          title: `Tip ${tipCount}`,
          columnId: `tip-${response_id}`,
          ...otherProps,
        }),
        scope: 'col',
        className: 'th_spp_tip',
      });
      tipCount += 1;
    });
  });

  const { isTeacher, isAdmin, isCoordinator } = userData;
  const isTeacherOrAdminOrCoordinator = isTeacher || isAdmin || isCoordinator;
  const studentScoringCondition = isTeacherOrAdminOrCoordinator;

  return (
    <div className="SPPStudentPerformanceTable">
      <div className="table-wrap">
        <table
          ref={studentsTable}
          className={cn('table--lined')}
          cellSpacing="0"
        >
          <thead>
            <tr>
              {headerOne.map((h) => (
                <th key={h.key} {...h} />
              ))}
            </tr>
            <tr>
              <th scope="col" className="th_name">
                <MatrixColumSort columnId="name" {...otherProps}>
                  Name
                </MatrixColumSort>
              </th>
              <th scope="col" className="th_points">
                <MatrixColumSort columnId="pointEarned" {...otherProps}>
                  Parts answered <br />
                  (of {(items || []).length})
                </MatrixColumSort>
              </th>
              <th scope="col" className="th_submitted">
                <MatrixColumSort columnId="submitted" {...otherProps}>
                  {studentScoringCondition && 'Submitted'}
                </MatrixColumSort>
              </th>
              {headerTwo.map((h) => (
                <th key={h.key} {...h} />
              ))}
            </tr>
          </thead>
          <tbody>
            {studentItems.map((student) => {
              const score = Number.parseInt(
                get(resultsByUser[student.id], 'score', 0),
                10,
              );
              const studentName = `${student.firstName} ${student.lastName}`;
              const submittedAt = get(
                studentSessionsByUserId,
                `[${student.id}].submittedAt`,
                0,
              );

              return (
                <tr key={student.id}>
                  <th className="th_student_names">
                    {!!submittedAt ? (
                      <Link
                        to={{
                          pathname: `/${
                            params.subject
                          }/assessments/spp/review/${
                            params.assignmentId
                          }${getPriorStudentsSubdir(routes)}/students/${
                            student.id
                          }`,
                          state: { returnPath },
                        }}
                      >
                        {studentName}
                      </Link>
                    ) : (
                      studentName
                    )}
                  </th>
                  <td className="td_points">{score}</td>
                  <td className="td_submitted_time">
                    {itemResultsByUser[student.id] && !!submittedAt ? (
                      <div className={cn('td_submitted_time_content')}>
                        {dateTimeFormat(
                          dayjs.utc(submittedAt),
                          'dateAndTimeShort',
                          assignment.timezone,
                        )}
                        {isStudentSubmissionLate({
                          submitted:
                            studentSessionsByUserId[student.id].submittedAt,
                          customSetting: customSettingsByStudentId[student.id],
                          dueAt: assignment.dueAt,
                        }) && (
                          <span className="ml-1 font-medium text-red-600">
                            (Late)
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className={cn('td_submitted_time_content')}>-</div>
                    )}
                  </td>
                  <SPPStudentPerformanceAnswers
                    student={student}
                    parts={items}
                    sessionTemplateItemsByRef={sessionTemplateItemsByRef}
                    studentResponses={studentResponses}
                    studentSessionsByUserId={studentSessionsByUserId}
                  />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

SPPStudentPerformanceTable.propTypes = {
  items: PropTypes.array.isRequired,
  params: PropTypes.shape({
    assignmentId: PropTypes.string,
    subject: PropTypes.string,
  }).isRequired,
  assignment: PropTypes.shape({
    timer: PropTypes.number,
  }).isRequired,
};

export default connect((state) => ({
  userData: state.userData,
}))(SPPStudentPerformanceTable);
